/**
 * History Box — archiving, listing, resuming, and deleting closed
 * multi-chat (linked-tile) groups. See MultiChatSession in lib/db/schema.ts
 * for the row shape and the archived/karma_study status split.
 */
import { db } from "@/lib/db";
import type { MultiChatSession } from "@/lib/db/schema";
import { getUserId } from "@/lib/user/identity";

const KARMA_STUDY_CONSENT_KEY = "karma_study_consent_deleted_multichat";

/** Off by default — deleting a session permanently wipes it unless the
 *  user has explicitly opted in to letting Karma study deleted history. */
export function getKarmaStudyConsent(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(KARMA_STUDY_CONSENT_KEY) === "true";
}

export function setKarmaStudyConsent(value: boolean) {
  if (typeof window === "undefined") return;
  localStorage.setItem(KARMA_STUDY_CONSENT_KEY, String(value));
}

interface TileInfoForArchive {
  id: string;
  name: string;
  profile?: { id: string; name: string } | null;
}

/** Called once every tile in a linked group has closed. Reads each tile's
 *  own messages (still under its own conversationId — this doesn't move or
 *  copy them, just indexes the group) and writes one History Box entry.
 *  A no-op if nothing was ever actually said in the group. */
export async function archiveClosedGroup(
  tiles: TileInfoForArchive[],
  primaryTileId: string | null
): Promise<void> {
  if (tiles.length < 2) return;
  const userId = getUserId();

  let messageCount = 0;
  let earliestCreatedAt = Date.now();
  let preview = "";
  try {
    for (const tile of tiles) {
      const msgs = await db.chatMessages.where("conversationId").equals(tile.id).sortBy("createdAt");
      messageCount += msgs.length;
      if (msgs.length > 0) {
        earliestCreatedAt = Math.min(earliestCreatedAt, msgs[0].createdAt ?? Date.now());
        const last = msgs[msgs.length - 1];
        if (last?.content) preview = last.content.slice(0, 140);
      }
    }
  } catch (err) {
    console.error("[history box] failed reading tile messages for archive:", err);
    return;
  }
  if (messageCount === 0) return;

  const session: MultiChatSession = {
    id: crypto.randomUUID(),
    userId,
    tileSnapshots: tiles.map((t) => ({
      tileId: t.id,
      tileName: t.name,
      profileId: t.profile?.id ?? "",
      profileName: t.profile?.name ?? t.name,
    })),
    primaryTileId: primaryTileId && tiles.some((t) => t.id === primaryTileId) ? primaryTileId : null,
    createdAt: earliestCreatedAt,
    closedAt: Date.now(),
    messageCount,
    preview,
    status: "archived",
  };
  try {
    await db.multiChatSessions.add(session);
  } catch (err) {
    console.error("[history box] failed to archive linked group:", err);
  }
}

export async function listArchivedSessions(): Promise<MultiChatSession[]> {
  const userId = getUserId();
  const rows = await db.multiChatSessions
    .where("userId")
    .equals(userId)
    .and((s) => s.status === "archived")
    .sortBy("closedAt");
  return rows.reverse();
}

/** Delete respects the user's consent setting: consented → soft-delete
 *  (hidden from the visible list, messages and row both kept for Karma to
 *  study); not consented → hard delete of the row and every tile's
 *  messages in the group. */
export async function deleteSession(sessionId: string): Promise<void> {
  const session = await db.multiChatSessions.get(sessionId);
  if (!session) return;

  if (getKarmaStudyConsent()) {
    await db.multiChatSessions.update(sessionId, { status: "karma_study" });
    return;
  }

  await db.multiChatSessions.delete(sessionId);
  for (const tile of session.tileSnapshots) {
    try {
      await db.chatMessages.where("conversationId").equals(tile.tileId).delete();
    } catch (err) {
      console.error("[history box] failed to hard-delete tile messages:", err);
    }
  }
}
